// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { withRouter } from 'react-router';
// eslint-disable-next-line import/named
import { injectIntl, type IntlShape } from 'react-intl';

// Components
import AppMask from './AppMask';
import AppRouter from './AppRouter';
import FeatureToggleLoader from 'components/atoms/Feature/FeatureToggleLoader';
import { withSidebar } from 'components/pages/User/Sidebar/SidebarContext/SidebarProvider';
import { withEasterEgg } from 'components/common/decorators/easterEgg/EasterEgg';
import FatalError from 'components/api/components/FatalError/FatalError';
import { Icon } from 'components/atoms/Icon/';
import Button from 'components/atoms/Button';
import Modal from 'components/atoms/Modal';
import qs, { EXPERIMENTS_FLAG } from 'store/entities/QueryString';

// Selectors
import * as errorFlags from 'store/reducers/errors/selectors';
import { currentPath } from 'store/reducers/navigation/selectors';
import { getLocaleCode } from 'store/reducers/dealerLocale/selectors';
import { isPrivateRoute } from 'store/reducers/userInteractions/selectors';
import { getExternalLoanerProvider } from 'store/reducers/dealerSettings/selectors';
import { getTransportationOption } from 'store/reducers/appointment/selectors';
// Utils
import { setLanguageTag } from 'utils/DOM';

// Constants
import {
  APPOINTMENT_ROUTE,
  SERVICES_ROUTE,
  DROPOFF_ROUTE,
  CONFIRMATION_ROUTE,
  REVIEW_ROUTE,
  ROUTES_ADA_TITLE_MAP
} from 'constants/routes';
import { TRANSPORTATION } from 'constants/transportation';

// Actions
import { gtmUserEvent } from 'store/reducers/gtm/actions';

// Types
import { gtmUserEventType } from 'types/gtm';

import './App.scss';

let Overlay;
if (qs.getBoolean(EXPERIMENTS_FLAG)) {
  Overlay = FeatureToggleLoader;
}

const { LOANER } = TRANSPORTATION;

type Props = {
  currentPath: string,
  servicesError: boolean,
  apptOptionErrors: boolean,
  dayTimeErrors: boolean,
  gtmUserEvent: gtmUserEventType,
  localeCode: string,
  intl: IntlShape,
  isEnabled: boolean,
  externalLoanerProvider: string,
  transportationOption: string
};

class App extends React.Component<Props> {
  constructor(props) {
    super(props);
    this.dialogClick = this.dialogClick.bind(this);
    // this.listenKeyboard = this.listenKeyboard.bind(this);
    this.closeModal = this.closeModal.bind(this);
    (this: any).fatalErrorPopup = React.createRef();
    (this: any).closeFrameAlert = React.createRef();
  }

  componentDidMount() {
    const { localeCode, currentPath } = this.props;
    setLanguageTag(localeCode);
    this.changeDocumentTitle(currentPath);
    // this.pendoInitialize();
  }

  componentDidUpdate() {
    const {
      servicesError,
      dayTimeErrors,
      apptOptionErrors,
      currentPath,
      gtmUserEvent,
      externalLoanerProvider,
      transportationOption
    } = this.props;
    this.changeDocumentTitle(currentPath);
    if (
      (servicesError && currentPath === SERVICES_ROUTE) ||
      (dayTimeErrors &&
        currentPath === APPOINTMENT_ROUTE &&
        (transportationOption !== LOANER || !externalLoanerProvider)) ||
      (apptOptionErrors && currentPath === DROPOFF_ROUTE) ||
      (apptOptionErrors && currentPath === CONFIRMATION_ROUTE)
    ) {
      (this: any).fatalErrorPopup.current.setShowModal(true);
      gtmUserEvent(
        'Pop Up Error',
        'Error Message',
        'Pop Up Error Show Up',
        'GTM_CURRENT_PAGE'
      );
    }
  }

  closeFrameAlert: React.Ref<any>;
  closeModal() {
    window.top.postMessage('closeC8Modal', '*');
  }

  dialogClick(event) {
    event.stopPropagation();
  }

  changeDocumentTitle = path => {
    const { intl } = this.props;
    document.title = intl.formatMessage(ROUTES_ADA_TITLE_MAP[path]);
  };

  showCloseFrameAlert = (show: boolean = true) => {
    const { currentPath } = this.props;
    if (currentPath === REVIEW_ROUTE) {
      (this: any).closeFrameAlert.current.setShowModal(show);
      return null;
    }
    this.closeModal();
  };

  getLeaveModal = () => {
    const { intl } = this.props;
    const modalView = qs.get('vmode') === 'modal';
    const nsCloseAlert = 'framedModal-closeAlert';
    if (!modalView) {
      return null;
    }
    return (
      <Modal
        key="leaveModal"
        ref={this.closeFrameAlert}
        size="large"
        showCloseButton={false}
        className={nsCloseAlert}
        title={() => {
          return intl.formatMessage({
            id: 'c8.self.some.title',
            defaultMessage: 'Are you sure you want to leave this page?'
          });
        }}
      >
        <div className={`${nsCloseAlert}__options`}>
          <Button
            onClick={() => {
              this.showCloseFrameAlert(false);
              this.closeModal();
            }}
            buttonTheme={'link'}
            className={`${nsCloseAlert}__options-leave`}
          >
            {intl.formatMessage({
              id: 'c8.self.some.leave.label',
              defaultMessage: 'Leave'
            })}
          </Button>
          <Button
            onClick={() => this.showCloseFrameAlert(false)}
            className={`${nsCloseAlert}__options-stay`}
            buttonTheme={'default'}
          >
            {intl.formatMessage({
              id: 'c8.self.some.stay.label',
              defaultMessage: 'Stay'
            })}
          </Button>
        </div>
      </Modal>
    );
  };

  getApp = () => {
    const modalView = qs.get('vmode') === 'modal';
    if (modalView) {
      return (
        <div
          key="framedModal"
          className="framed-modal-wrapper"
          onClick={() => this.showCloseFrameAlert()}
        >
          <div
            className={`framedModal__close`}
            onClick={() => this.showCloseFrameAlert()}
            onKeyUp={e => {
              if (e.key === 'Enter') {
                this.showCloseFrameAlert();
              }
            }}
          >
            <Icon type="close" />
          </div>
          <div className="framed-modal" onClick={this.dialogClick}>
            <AppRouter key="router" />
          </div>
        </div>
      );
    } else {
      return (
        <div className="framed-modal" onClick={this.dialogClick}>
          <AppRouter key="router" />
        </div>
      );
    }
  };

  pendoInitialize = () => {
    window.pendo &&
      window.pendo.initialize({
        visitor: {
          id: 'VISITOR-UNIQUE-ID'
        }
      });
  };

  render() {
    // prettier-ignore
    const { intl } = this.props;

    return [
      <FatalError
        key="fatalError"
        ref={(this: any).fatalErrorPopup}
        intl={intl}
      />,
      this.getLeaveModal(),
      this.getApp(),
      <AppMask key="mask" />,
      Overlay && <Overlay key="overlay" />
    ];
  }
}

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ gtmUserEvent }, dispatch);

// @TODO: Find another way to prevent the navigation bug (DE97776)
// other than forcing a re-render with the isEnabled prop

const mapStateToProps = state => ({
  currentPath: currentPath(state),
  servicesError: errorFlags.errorLoadingServices(state),
  dayTimeErrors: errorFlags.errorLoadingDayTime(state),
  apptOptionErrors: errorFlags.errorLoadingApptOption(state),
  localeCode: getLocaleCode(state),
  isEnabled: isPrivateRoute(state),
  externalLoanerProvider: getExternalLoanerProvider(state),
  transportationOption: getTransportationOption(state)
});

// export default App;
export default compose(
  withRouter,
  withSidebar,
  withEasterEgg,
  injectIntl,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(App);



// WEBPACK FOOTER //
// ./src/components/consumer8/App.js